// src/app/ui-components/sidebars/table-of-contents/table-of-contents.component.ts

import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewChildren,
  QueryList,
  Inject,
  PLATFORM_ID,
  NgZone,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { SlugifyPipe } from '@pipes/slugify.pipe';
import { filter, Subscription, startWith, take } from 'rxjs';
import { TocNavService } from '@services/toc-nav.service';
import { TocApiService } from '@services/toc-api.service';
import { HighlightTextPipe } from '@pipes/highlight-text.pipe';
import { ViewEncapsulation } from '@angular/core';

interface TocEntry {
  type: string;
  slug: string;
  toc_title: string;
  path_segments: string[];
  toc_order?: number;
  display_order?: number;
  title?: string;
}

interface ModuleOption {
  label: string;
  slug: string;
}

type TocApiResponse = { toc?: TocEntry[] } | TocEntry[] | null | undefined;

@Component({
  selector: 'app-table-of-contents',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HighlightTextPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './table-of-contents.component.html',
  styleUrls: ['./table-of-contents.component.scss'],
  providers: [SlugifyPipe],
  encapsulation: ViewEncapsulation.None,
})
export class TableOfContentsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  toc: TocEntry[] = [];
  modules: ModuleOption[] = [];
  selectedModule = '';

  isLoading = true;
  loadError: string | null = null;

  /** Search */
  searchTerm = '';
  get hasSearch(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  /** Animated category indicator refs */
  @ViewChild('tocList', { static: false }) tocList?: ElementRef<HTMLElement>;
  @ViewChildren('catEl') catEls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('searchInput', { static: false })
  searchInputRef?: ElementRef<HTMLInputElement>;
  private openedSub?: Subscription;

  /** Currently highlighted subject/category (for the moving bar) */
  activeSubject: string | null = null;
  activeCategory: string | null = null;

  private subjectsMap = new Map<string, string[]>(); // moduleSlug -> subjects[]
  private categoriesMap = new Map<string, Map<string, string[]>>(); // moduleSlug -> (subject -> categories[])
  private entriesMap = new Map<string, Map<string, Map<string, TocEntry[]>>>(); // moduleSlug -> (subject -> (category -> entries[]))

  private collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  private subjectOrder: string[] = [
    'Biochemistry',
    'Immunology',
    'Microbiology',
    'Cardiovascular',
    'Endocrine',
    'Gastrointestinal',
    'Hematology and Oncology',
    'Musculoskeletal, Skin, and Connective Tissue',
    'Neurology and Special Senses',
    'Psychiatry',
    'Renal',
    'Reproductive',
    'Respiratory',
  ];

  private categoryOrder: Record<string, string[]> = {};

  private rank = (
    order: string[] | undefined,
    value: string | null | undefined,
  ) => (order ? order.indexOf((value ?? '').toString()) : -1);

  private apiSub?: Subscription;
  private navSub?: Subscription;
  private catChangesSub?: Subscription;

  private readonly isBrowser: boolean;

  constructor(
    private router: Router,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private slugifyPipe: SlugifyPipe,
    private host: ElementRef<HTMLElement>,
    private tocNav: TocNavService,
    private tocApi: TocApiService,
    @Inject(PLATFORM_ID) pid: Object,
    @Inject(DOCUMENT) private doc: Document,
  ) {
    this.isBrowser = isPlatformBrowser(pid);
  }

  public markTocClick(): void {
    this.tocNav.markClick();
  }

  onSearchInput(): void {
    this.cdr.markForCheck();
    this.afterNextStableRender(() => {
      this.deferPositionIndicator();
      this.deferScrollToActive();
    });
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.onSearchInput();
  }

  private needle(): string {
    return this.searchTerm.trim().toLowerCase();
  }

  private includes(haystack: string, needle: string): boolean {
    if (!needle) return true;
    return haystack.toLowerCase().includes(needle);
  }

  private entryMatches(
    subject: string,
    category: string | null,
    e: TocEntry,
    needle: string,
  ): boolean {
    if (!needle) return true;

    const subjHit = this.includes(subject ?? '', needle);
    const catHit = this.includes(category ?? '', needle);
    if (subjHit || catHit) return true;

    const title = (e.toc_title ?? e.title ?? '').toString();
    if (this.includes(title, needle)) return true;

    if (this.includes(e.slug ?? '', needle)) return true;

    return false;
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.loadError = null;

    this.apiSub?.unsubscribe();
    this.apiSub = this.tocApi.get().subscribe({
      next: (res: TocApiResponse) => {
        // Some services emit a null/undefined placeholder before the real payload.
        const parsed = this.parseTocResponse(res);
        if (!parsed.ready) return;

        // Ensure UI updates even if the service resolves outside Angular’s zone.
        this.zone.run(() => {
          try {
            this.toc = parsed.toc;

            const tocWithValidPaths = this.toc.filter(
              (e) =>
                Array.isArray(e.path_segments) &&
                typeof e.path_segments[0] === 'string' &&
                e.path_segments[0].trim().length > 0,
            );

            const uniqueLabels = Array.from(
              new Set(tocWithValidPaths.map((e) => e.path_segments[0])),
            );

            this.modules = uniqueLabels.map((label) => ({
              label,
              slug: this.slugifyPipe.transform(label),
            }));

            this.buildMemoMaps();

            // Derive selection/active state from URL
            this.syncFromUrl();

            this.loadError = null;
          } catch (err) {
            console.error(err);
            this.loadError = 'Failed to render table of contents.';
            this.toc = [];
            this.modules = [];
            this.selectedModule = '';
            this.activeSubject = null;
            this.activeCategory = null;
          } finally {
            this.isLoading = false;

            // OnPush: render now, then measure after the DOM settles
            this.cdr.detectChanges();
            this.afterNextStableRender(() => {
              this.deferScrollToActive();
              this.deferPositionIndicator();
            });
          }
        });
      },
      error: (err: unknown) => {
        console.error(err);
        this.zone.run(() => {
          this.loadError = 'Failed to load table of contents.';
          this.isLoading = false;
          this.cdr.markForCheck();
        });
      },
    });
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.openedSub?.unsubscribe();
    this.openedSub = this.tocNav.opened$.subscribe(() => {
      //this.focusSearch(true);
    });

    // React when categories first render / change (async TOC load, module change)
    this.catChangesSub?.unsubscribe();
    this.catChangesSub = this.catEls.changes
      .pipe(startWith(this.catEls))
      .subscribe(() => {
        this.afterNextStableRender(() => {
          this.deferPositionIndicator();
        });
      });

    // Catch initial hydration state as well as later navigations
    this.navSub?.unsubscribe();
    this.navSub = this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        startWith(null as unknown as NavigationEnd),
      )
      .subscribe(() => {
        this.syncFromUrl();
        this.cdr.markForCheck();

        this.afterNextStableRender(() => {
          this.deferPositionIndicator();

          if (this.tocNav.consumeClickFlag()) {
            this.scrollMainContentToTop();
          } else {
            this.deferScrollToActive();
          }
        });
      });
  }

  ngOnDestroy(): void {
    this.apiSub?.unsubscribe();
    this.navSub?.unsubscribe();
    this.catChangesSub?.unsubscribe();
    this.openedSub?.unsubscribe();
  }

  private parseTocResponse(res: TocApiResponse): {
    ready: boolean;
    toc: TocEntry[];
  } {
    if (res == null) return { ready: false, toc: [] };

    if (Array.isArray(res)) {
      return { ready: true, toc: res };
    }

    if (typeof res === 'object') {
      const toc = Array.isArray((res as { toc?: unknown }).toc)
        ? ((res as { toc?: unknown }).toc as TocEntry[])
        : [];
      return { ready: true, toc };
    }

    return { ready: true, toc: [] };
  }

  private readEntriesUrlParams(): {
    module: string | null;
    slug: string | null;
  } {
    const tree = this.router.parseUrl(this.router.url);
    const segs =
      tree.root.children['primary']?.segments.map((s) => s.path) ?? [];

    const idx = segs.indexOf('entries');
    if (idx === -1) return { module: null, slug: null };

    const module = segs[idx + 1] ?? null;
    const slug = segs[idx + 2] ?? null;
    return { module, slug };
  }

  private syncFromUrl(): void {
    if (!this.modules.length) return;

    const { module, slug } = this.readEntriesUrlParams();

    this.selectedModule =
      module && this.modules.some((m) => m.slug === module)
        ? module
        : this.modules[0]?.slug || '';

    if (slug) {
      this.deriveActiveCategoryFromSlug(slug);
    } else {
      this.activeSubject = null;
      this.activeCategory = null;
    }
  }

  private deriveActiveCategoryFromSlug(slug: string): void {
    const entry = this.toc.find((e) => {
      const modLabel = e.path_segments?.[0] ?? '';
      const modSlug = this.slugifyPipe.transform(modLabel);
      return modSlug === this.selectedModule && e.slug === slug;
    });

    if (!entry) {
      this.activeSubject = null;
      this.activeCategory = null;
      return;
    }

    this.activeSubject = entry.path_segments?.[1] ?? null;
    this.activeCategory = entry.path_segments?.[2] ?? '';
  }

  private afterNextStableRender(fn: () => void): void {
    if (!this.isBrowser) return;
    this.zone.onStable.pipe(take(1)).subscribe(() => {
      requestAnimationFrame(() => fn());
    });
  }

  onTocScroll(): void {
    if (!this.isBrowser) return;
    this.positionIndicator();
  }

  selectCategory(subject: string, category: string | null, _ev?: Event): void {
    this.activeSubject = subject;
    this.activeCategory = category ?? '';
    this.positionIndicator();
  }

  isActiveCategory(subject: string, category: string | null): boolean {
    return (
      this.activeSubject === subject &&
      (this.activeCategory ?? '') === (category ?? '')
    );
  }

  trackBySubject = (_: number, s: string) => s;
  trackByCategory = (_: number, c: string) => c ?? '';
  trackByEntry = (_: number, e: TocEntry) => e.slug;

  private deferPositionIndicator(maxTries = 30, delayMs = 50): void {
    if (!this.isBrowser) return;
    let tries = 0;
    const tick = () => {
      requestAnimationFrame(() => {
        const ok = this.positionIndicator();
        if (!ok && ++tries < maxTries) setTimeout(tick, delayMs);
      });
    };
    setTimeout(tick, 0);
  }

  private positionIndicator(): boolean {
    if (!this.isBrowser) return false;

    const listEl = this.tocList?.nativeElement;
    const cats = this.catEls?.toArray() ?? [];
    if (!listEl || !cats.length) return false;

    // Searching: don’t show/move the indicator (it becomes noisy).
    if (this.hasSearch) {
      listEl.style.setProperty('--bar-top', `0px`);
      listEl.style.setProperty('--bar-height', '0px');
      return true;
    }

    let targetEl: HTMLElement | null =
      cats
        .map((r) => r.nativeElement)
        .find((el) => el.classList.contains('is-active')) ?? null;

    if (!targetEl) {
      targetEl =
        cats
          .map((r) => r.nativeElement)
          .find((el) => !!el.querySelector('a.active')) ?? null;
    }

    if (!targetEl) {
      listEl.style.setProperty('--bar-top', `0px`);
      listEl.style.setProperty('--bar-height', '0px');
      return false;
    }

    const tRect = targetEl.getBoundingClientRect();
    const lRect = listEl.getBoundingClientRect();
    const top = listEl.scrollTop + (tRect.top - lRect.top);
    const height = tRect.height;

    listEl.style.setProperty('--bar-top', `${top}px`);
    listEl.style.setProperty('--bar-height', `${height}px`);
    return true;
  }

  private scrollMainContentToTop(): void {
    if (!this.isBrowser) return;

    const scroller =
      this.doc.querySelector<HTMLElement>('.main-content') ??
      (this.doc.scrollingElement as HTMLElement | null);

    if (!scroller) return;

    const isScrollable = scroller.scrollHeight > scroller.clientHeight;
    if (isScrollable) scroller.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private isScrollable(el: HTMLElement): boolean {
    if (!this.isBrowser) return false;
    const s = getComputedStyle(el);
    const canScroll = /(auto|scroll|overlay)/.test(s.overflowY);
    return canScroll && el.scrollHeight > el.clientHeight;
  }

  private buildMemoMaps(): void {
    this.subjectsMap.clear();
    this.categoriesMap.clear();
    this.entriesMap.clear();

    for (const moduleOption of this.modules) {
      const slug = moduleOption.slug;

      const entriesForModule = this.toc.filter((e) => {
        const modLabel = e.path_segments?.[0] ?? '';
        return this.slugifyPipe.transform(modLabel) === slug;
      });

      const subjects = Array.from(
        new Set(entriesForModule.map((e) => e.path_segments?.[1])),
      ).filter((s): s is string => !!s);
      this.subjectsMap.set(slug, subjects);

      const catMap = new Map<string, string[]>();
      const entryMap = new Map<string, Map<string, TocEntry[]>>();

      for (const subj of subjects) {
        const entriesForSubj = entriesForModule.filter(
          (e) => e.path_segments?.[1] === subj,
        );

        // Include empty-string category so "Uncategorized" actually renders.
        const categories = Array.from(
          new Set(
            entriesForSubj.map((e) => (e.path_segments?.[2] ?? '').toString()),
          ),
        );

        catMap.set(subj, categories);

        const subMap = new Map<string, TocEntry[]>();
        for (const cat of categories) {
          subMap.set(
            cat,
            entriesForSubj.filter((e) => (e.path_segments?.[2] ?? '') === cat),
          );
        }
        entryMap.set(subj, subMap);
      }

      this.categoriesMap.set(slug, catMap);
      this.entriesMap.set(slug, entryMap);
    }
  }

  private getScrollParent(el: HTMLElement | null): HTMLElement | null {
    if (!this.isBrowser) return null;
    let node: HTMLElement | null = el;
    while (
      node &&
      node !== this.doc.body &&
      node !== this.doc.documentElement
    ) {
      if (this.isScrollable(node)) return node;
      node = node.parentElement;
    }
    return this.doc.scrollingElement as HTMLElement | null;
  }

  private resolveTocContainer(activeEl: HTMLElement): HTMLElement | null {
    if (!this.isBrowser) return null;

    const listEl = this.tocList?.nativeElement ?? null;
    if (listEl && listEl.contains(activeEl)) {
      if (this.isScrollable(listEl)) return listEl;
      const parent = this.getScrollParent(listEl);
      if (parent) return parent;
    }

    const parent = this.getScrollParent(activeEl);
    if (parent) return parent;

    return null;
  }

  private deferScrollToActive(maxTries = 10, delayMs = 50): void {
    if (!this.isBrowser) return;
    let tries = 0;
    const tick = () => {
      requestAnimationFrame(() => {
        const ok = this.scrollActiveIntoView();
        if (!ok && ++tries < maxTries) setTimeout(tick, delayMs);
      });
    };
    setTimeout(tick, 0);
  }

  private scrollActiveIntoView(): boolean {
    if (!this.isBrowser) return false;

    // If searching, don’t auto-scroll to active (it’s often filtered out / confusing).
    if (this.hasSearch) return true;

    const root = this.host.nativeElement;
    const active = root.querySelector<HTMLElement>('.toc a.active');
    if (!active) return false;

    const container = this.resolveTocContainer(active);
    if (!container) return false;

    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();

    if (aRect.top >= cRect.top && aRect.bottom <= cRect.bottom) return true;

    const target =
      container.scrollTop +
      (aRect.top - cRect.top) -
      (container.clientHeight / 2 - aRect.height / 2);

    const clamped = Math.max(
      0,
      Math.min(target, container.scrollHeight - container.clientHeight),
    );

    container.scrollTo({ top: clamped, behavior: 'smooth' });
    return true;
  }

  onModuleChange(newModule: string): void {
    this.searchTerm = ''; // reset search when switching modules
    const { slug } = this.readEntriesUrlParams();
    const commands: any[] = ['/entries', newModule];
    if (slug) commands.push(slug);
    this.router.navigate(commands);
  }

  private sortStringsWithOrder(list: string[], order?: string[]): string[] {
    return [...list].sort((a: string, b: string) => {
      const ra = this.rank(order, a);
      const rb = this.rank(order, b);
      if (ra !== -1 || rb !== -1) {
        const va = ra === -1 ? Number.MAX_SAFE_INTEGER : ra;
        const vb = rb === -1 ? Number.MAX_SAFE_INTEGER : rb;
        return va - vb || this.collator.compare(a, b);
      }
      return this.collator.compare(a, b);
    });
  }

  getSubjects(): string[] {
    const subjects = this.subjectsMap.get(this.selectedModule) ?? [];
    const sorted = this.sortStringsWithOrder(subjects, this.subjectOrder);

    const n = this.needle();
    if (!n) return sorted;

    return sorted.filter((subject) => {
      const categories = this.getCategories(subject);
      return categories.length > 0;
    });
  }

  getCategories(subject: string): string[] {
    const categories =
      this.categoriesMap.get(this.selectedModule)?.get(subject) ?? [];
    const preferred = this.categoryOrder[subject];
    const sorted = this.sortStringsWithOrder(categories, preferred);

    const n = this.needle();
    if (!n) return sorted;

    // If subject itself matches, keep all categories (faster mental model).
    if (this.includes(subject, n)) return sorted;

    return sorted.filter((category) => {
      const entries = this.getEntries(subject, category);
      return entries.length > 0;
    });
  }

  getEntries(subject: string, category: string | null): TocEntry[] {
    const list =
      this.entriesMap
        .get(this.selectedModule)
        ?.get(subject)
        ?.get(category ?? '') ?? [];

    const sorted = [...list].sort((a: TocEntry, b: TocEntry) => {
      const ao = a.toc_order ?? a.display_order ?? Number.MAX_SAFE_INTEGER;
      const bo = b.toc_order ?? b.display_order ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;

      const at = a.toc_title ?? a.title ?? '';
      const bt = b.toc_title ?? b.title ?? '';
      return this.collator.compare(at, bt);
    });

    const n = this.needle();
    if (!n) return sorted;

    // If category matches, show all entries in it.
    if (this.includes(category ?? '', n)) return sorted;

    return sorted.filter((e) => this.entryMatches(subject, category, e, n));
  }

  get hasResults(): boolean {
    return this.getSubjects().length > 0;
  }

  // private focusSearch(selectExisting: boolean): void {
  //   if (!this.isBrowser) return;

  //   const tryFocus = (tries = 0) => {
  //     const el = this.searchInputRef?.nativeElement;

  //     if (!el) {
  //       if (tries < 20) setTimeout(() => tryFocus(tries + 1), 50);
  //       return;
  //     }

  //     // wait for DOM/layout to settle (sheet animation + outlet render)
  //     requestAnimationFrame(() => {
  //       try {
  //         el.focus({ preventScroll: true });
  //       } catch {
  //         el.focus();
  //       }

  //       if (selectExisting && el.value) {
  //         el.select();
  //         // iOS Safari sometimes needs setSelectionRange to “stick”
  //         try {
  //           el.setSelectionRange(0, el.value.length);
  //         } catch {}
  //       }
  //     });
  //   };

  //   this.afterNextStableRender(() => tryFocus());
  // }
}
